import type { LayerDocument, MapDocument, TerrainLayer } from "../document/types.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { paintLabel, terrainEntryLabel } from "./catalogue.ts";
import type { TerrainNamer } from "./terrain-names.ts";

/**
 * Which terrain layers follow their terrain's name, and the in-memory tidy a world takes when it opens (ADR-038
 * amendment decisions 5 and 6). A following layer carries `autoName: true` and its entry's display name; a
 * layer the author named carries `false` or, when saved without the flag, a name no generated form matches.
 */

/**
 * The colour of a terrain whose entry label or display name a layer name is.
 *
 * @param name The layer's name.
 * @param terrain Its terrain.
 * @param namer The world's namer.
 * @returns The colour index, or undefined when the name is neither for any colour.
 */
export function colourNamed(name: string, terrain: TerrainDefinition, namer: TerrainNamer): number | undefined {
  const count = terrain.colors.length;
  return terrain.colors.find((c) => (count > 1 && c.name !== "" && name === terrainEntryLabel(terrain.name, c.name, count)) || name === namer.name(terrain.id, c.index))?.index;
}

/**
 * Whether a terrain layer follows its terrain's name. The flag decides when present. Without it the layer
 * follows when its name is blank, its id, its set's name as the pack writes it or as `paintLabel` reads it, or
 * a generated name of any colour of its terrain (`colourNamed`); a layer whose terrain the context lacks
 * keeps its name.
 *
 * @param layer The layer.
 * @param terrain Its terrain, or undefined when the context lacks it.
 * @param namer The world's namer.
 * @returns True when the editor names the layer.
 */
export function followsTerrain(layer: TerrainLayer, terrain: TerrainDefinition | undefined, namer: TerrainNamer): boolean {
  if (layer.autoName !== undefined) {
    return layer.autoName;
  }
  if (terrain === undefined) {
    return false;
  }
  const name = layer.name.trim();
  return name === "" || name === layer.id || name === terrain.name || name === paintLabel(terrain.name) || colourNamed(name, terrain, namer) !== undefined;
}

/**
 * The highest colour a terrain layer's corners hold, 0 when none is painted (ADR-038 decision 4).
 *
 * @param layer The layer.
 * @returns The colour.
 */
export function paintedColour(layer: TerrainLayer): number {
  let colour = 0;
  for (let i = 0; i < layer.corners.length; i += 1) {
    colour = Math.max(colour, layer.corners[i] as number);
  }
  return colour;
}

/**
 * The colour a following layer paints: its corners' colour, else the colour its name stands for, else its
 * set's first.
 *
 * @param layer The layer.
 * @param terrain Its terrain.
 * @param namer The world's namer.
 * @returns The colour index.
 */
export function followingColour(layer: TerrainLayer, terrain: TerrainDefinition, namer: TerrainNamer): number {
  const painted = paintedColour(layer);
  return painted !== 0 ? painted : (colourNamed(layer.name.trim(), terrain, namer) ?? terrain.colors[0]?.index ?? 1);
}

/**
 * Names every following terrain layer of a map after the entry it paints and flags it (ADR-038 amendment
 * decision 6). Layers the author named, and layers whose terrain the context lacks, are left as they are.
 *
 * @param map The map as read.
 * @param terrains The compile context's terrains.
 * @param namer The world's namer.
 * @returns The tidied map (the same object when nothing changed) and, for each following layer, the colour it
 *   paints (its corners', else the one its name stands for), to take as the layer's chosen colour: corners win
 *   while they hold paint, and once every corner is erased the layer still paints, and is named for, that
 *   colour.
 */
export function tidyTerrainLayers(map: MapDocument, terrains: readonly TerrainDefinition[], namer: TerrainNamer): { readonly map: MapDocument; readonly colours: ReadonlyMap<string, number> } {
  const byId = new Map(terrains.map((t) => [t.id, t]));
  const colours = new Map<string, number>();
  let changed = false;
  const layers = map.layers.map((layer): LayerDocument => {
    if (layer.kind !== "terrain") {
      return layer;
    }
    const terrain = byId.get(layer.terrain);
    if (terrain === undefined || !followsTerrain(layer, terrain, namer)) {
      return layer;
    }
    const colour = followingColour(layer, terrain, namer);
    colours.set(layer.id, colour);
    const name = namer.name(terrain.id, colour);
    if (layer.name === name && layer.autoName === true) {
      return layer;
    }
    changed = true;
    return { ...layer, name, autoName: true };
  });
  return { map: changed ? { ...map, layers } : map, colours };
}
